import api from './api';
import {
  AIBrandProfile,
  CreateAIBrandProfileInput,
  UpdateAIBrandProfileInput,
  AIBrandProfileResponse,
  AIBrandProfileVersion,
  AIBrandProfileDraft,
  AIBrandProfileDraftResponse,
  SaveAIBrandProfileDraftInput,
} from '../types/ai';

/** System cap of Brand World profiles per team (backend enforces it in every mode) */
export const BRAND_PROFILE_SYSTEM_MAX = 10;

export interface AIBrandProfileList {
  profiles: AIBrandProfile[];
  /** EFFECTIVE limit 0..10 (package limit only in enforce, otherwise 10) */
  brandProfileLimit: number;
}

/**
 * AI Brand Profile Service
 *
 * API client for AI brand profile management endpoints
 */

export const aiBrandProfileService = {
  /**
   * Get all brand profiles for current team
   * GET /api/ai/brand-profiles
   */
  getProfiles: async (): Promise<AIBrandProfileList> => {
    const { data } = await api.get<{ success: boolean; data: AIBrandProfile[]; brandProfileLimit?: number }>(
      '/api/ai/brand-profiles'
    );
    return { profiles: data.data, brandProfileLimit: data.brandProfileLimit ?? BRAND_PROFILE_SYSTEM_MAX };
  },

  /**
   * Get single brand profile by ID
   * GET /api/ai/brand-profiles/:id
   */
  getProfile: async (id: number): Promise<AIBrandProfile> => {
    const { data } = await api.get<AIBrandProfileResponse>(`/api/ai/brand-profiles/${id}`);
    return data.data;
  },

  /**
   * Create new brand profile
   * POST /api/ai/brand-profiles
   */
  createProfile: async (input: CreateAIBrandProfileInput): Promise<AIBrandProfile> => {
    const { data } = await api.post<AIBrandProfileResponse>('/api/ai/brand-profiles', input);
    return data.data;
  },

  /**
   * Update brand profile
   * PUT /api/ai/brand-profiles/:id
   */
  updateProfile: async (id: number, input: UpdateAIBrandProfileInput): Promise<AIBrandProfile> => {
    const { data } = await api.put<AIBrandProfileResponse>(`/api/ai/brand-profiles/${id}`, input);
    return data.data;
  },

  /**
   * Delete brand profile
   * DELETE /api/ai/brand-profiles/:id
   */
  deleteProfile: async (id: number): Promise<void> => {
    await api.delete(`/api/ai/brand-profiles/${id}`);
  },

  /**
   * Set profile as default
   * PUT /api/ai/brand-profiles/:id/default
   */
  setDefault: async (id: number): Promise<AIBrandProfile> => {
    const { data } = await api.put<AIBrandProfileResponse>(`/api/ai/brand-profiles/${id}/default`);
    return data.data;
  },

  /**
   * Activate a DRAFT profile
   * POST /api/ai/brand-profiles/:id/activate
   */
  activateProfile: async (id: number): Promise<AIBrandProfile> => {
    const { data } = await api.post<AIBrandProfileResponse>(`/api/ai/brand-profiles/${id}/activate`);
    return data.data;
  },

  /**
   * Get the version history for a profile, newest first
   * GET /api/ai/brand-profiles/:id/versions
   */
  getVersions: async (id: number): Promise<AIBrandProfileVersion[]> => {
    const { data } = await api.get<{ success: boolean; data: AIBrandProfileVersion[] }>(
      `/api/ai/brand-profiles/${id}/versions`
    );
    return data.data;
  },

  /**
   * Restore a profile to a prior version
   * POST /api/ai/brand-profiles/:id/versions/:versionNumber/restore
   */
  restoreVersion: async (id: number, versionNumber: number): Promise<AIBrandProfile> => {
    const { data } = await api.post<AIBrandProfileResponse>(
      `/api/ai/brand-profiles/${id}/versions/${versionNumber}/restore`
    );
    return data.data;
  },

  /**
   * Duplicate a profile into a new DRAFT copy
   * POST /api/ai/brand-profiles/:id/duplicate
   */
  duplicateProfile: async (id: number): Promise<AIBrandProfile> => {
    const { data } = await api.post<AIBrandProfileResponse>(`/api/ai/brand-profiles/${id}/duplicate`);
    return data.data;
  },

  /**
   * Get the current server-side draft for the "Create AI Brand Profile" wizard,
   * if one exists for this team. Enables resume-across-devices.
   * GET /api/ai/brand-profiles/draft
   */
  getDraft: async (): Promise<AIBrandProfileDraft | null> => {
    const { data } = await api.get<AIBrandProfileDraftResponse>('/api/ai/brand-profiles/draft');
    return data.data;
  },

  /**
   * Save (upsert) the current wizard draft to the server.
   * PUT /api/ai/brand-profiles/draft
   */
  saveDraft: async (input: SaveAIBrandProfileDraftInput): Promise<AIBrandProfileDraft> => {
    const { data } = await api.put<AIBrandProfileDraftResponse>('/api/ai/brand-profiles/draft', input);
    if (!data.data) {
      throw new Error('Failed to save draft: server returned no data');
    }
    return data.data;
  },

  /**
   * Discard the server-side draft.
   * DELETE /api/ai/brand-profiles/draft
   */
  deleteDraft: async (): Promise<void> => {
    await api.delete('/api/ai/brand-profiles/draft');
  },
};

export default aiBrandProfileService;
